import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { tap, catchError, filter } from 'rxjs/operators';
import { environment } from '../../environment';

export interface FIDataResponse {
  status?: string;
  message?: string;
  data?: any[];
  [key: string]: any;
}

@Injectable({
  providedIn: 'root'
})
export class FiDataService {

  private readonly baseUrl = environment.accountAggregator.baseUrl;

  private fiDataSubject = new BehaviorSubject<FIDataResponse | null>(null);
  public fiData$: Observable<FIDataResponse> = this.fiDataSubject.asObservable().pipe(
    filter((data): data is FIDataResponse => !!data)
  );

  private selectedAccountIdSubject = new BehaviorSubject<string | null>(null);
  public selectedAccountId$ = this.selectedAccountIdSubject.asObservable();

  private cashFlowSubject = new BehaviorSubject<{ incoming: number; outgoing: number }>({
    incoming: 0,
    outgoing: 0
  });
  public cashFlow$ = this.cashFlowSubject.asObservable();

  private isFetching = false;   // ✅ Prevent duplicate API calls

  constructor(private http: HttpClient) { }

  // -------------------- HEADERS --------------------

  private getHeaders(): HttpHeaders {
    const headerConfig = environment.accountAggregator as any;

    return new HttpHeaders({
      client_id: headerConfig.headers?.clientId || '',
      client_secret: headerConfig.headers?.clientSecret || '',
      organisationid: headerConfig.headers?.organisationId || '',
      appidentifier: headerConfig.headers?.appIdentifier || '',
      Accept: 'application/json',
      'content-type': 'application/json'
    });
  }

  // -------------------- API WITH CACHE LOGIC --------------------

  getAllFIData(
    consentID: string,
    loadtype: 'new' | 'cache' = 'cache'
  ): Observable<FIDataResponse> {

    // 🔹 Always call API
    if (loadtype === 'new') {
      this.isFetching = true;

      return this.http.post<FIDataResponse>(
        `${this.baseUrl}/getallfidata`,
        { consentID },
        { headers: this.getHeaders() }
      ).pipe(
        tap(response => {
          this.fiDataSubject.next(response);
          this.calculateCashFlow(this.selectedAccountIdSubject.value);
          this.isFetching = false;
        }),
        catchError(error => {
          this.isFetching = false;
          return throwError(() => new Error(error.message || 'Server error'));
        })
      );
    }

    // 🔹 Use Cache Logic
    else if (loadtype === 'cache') {

      const cachedData = this.fiDataSubject.getValue();

      // ✅ If cache exists → return immediately
      if (cachedData) {
        return of(cachedData);
      }

      // ✅ If not cached and not fetching → call API
      if (!this.isFetching) {
        this.isFetching = true;

        return this.http.post<FIDataResponse>(
          `${this.baseUrl}/getallfidata`,
          { consentID },
          { headers: this.getHeaders() }
        ).pipe(
          tap(response => {
            this.fiDataSubject.next(response);
            this.calculateCashFlow(this.selectedAccountIdSubject.value);
            this.isFetching = false;
          }),
          catchError(error => {
            this.isFetching = false;
            return throwError(() => new Error(error.message || 'Server error'));
          })
        );
      }

      // ✅ If already fetching → return observable
      return this.fiData$;
    }

    // ❌ Invalid loadtype
    return throwError(() => new Error('Invalid loadtype. Use "new" or "cache".'));
  }

  requestConsent(data: any): Observable<any> {
    return this.http.post(
      `${this.baseUrl}/v3/requestconsent`,
      data,
      { headers: this.getHeaders() }
    );
  }

  getConsentsList(data: {
    mobileNumber: string;
    productID: string;
    accountID?: string;
  }): Observable<any> {
    return this.http.post(
      `${this.baseUrl}/getconsentslist`,
      data,
      { headers: this.getHeaders() }
    );
  }

  revokeConsent(consentID: string): Observable<any> {
    return this.http.post(
      `${this.baseUrl}/revokeconsent`,
      { consentID },
      { headers: this.getHeaders() }
    );
  }

  // -------------------- ACCOUNT SELECTION --------------------

  setSelectedAccount(accountId: string | null): void {
    this.selectedAccountIdSubject.next(accountId);
    this.calculateCashFlow(accountId);
  }

  // -------------------- CASH FLOW CALCULATION --------------------

  private calculateCashFlow(accountId: string | null): void {

    const response = this.fiDataSubject.getValue();
    if (!response?.data || !Array.isArray(response.data)) return;

    let incoming = 0;
    let outgoing = 0;

    const accounts = accountId
      ? response.data.filter((x: any) => x.linkReferenceNumber === accountId)
      : response.data;

    accounts.forEach((item: any) => {
      item?.Transactions?.Transaction?.forEach((tx: any) => {
        const amount = parseFloat(tx.amount || 0);

        if (tx.type === 'DEBIT') outgoing += amount;
        if (tx.type === 'CREDIT') incoming += amount;
      });
    });

    this.cashFlowSubject.next({ incoming, outgoing });
  }

  // -------------------- CLEAR CACHE --------------------

  clearCache(): void {
    this.fiDataSubject.next(null);
  }

  // -------------------- GET CURRENT STATE --------------------

  getFiState(): FIDataResponse | null {
    return this.fiDataSubject.getValue();
  }
}